import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";

import { createLogger } from "@/lib/logging/logger";

const logger = createLogger("useJsonLocalStorage");

// Same-tab writes don't trigger the native "storage" event, so notify manually
// after every write. Subscribers match only on the event name and re-read their
// own key, so a bare Event (no StorageEvent payload) is sufficient.
const dispatchStorageEvent = () => {
  window.dispatchEvent(new Event("storage"));
};

const subscribe = (callback: () => void) => {
  window.addEventListener("storage", callback);
  return () => window.removeEventListener("storage", callback);
};

// In-memory fallback for writes that couldn't be persisted (quota exceeded,
// privacy mode, storage disabled). Keyed by storage key and shared at module
// scope — rather than per-hook-instance state — so every mounted instance for
// the same key (e.g. the same preference read in multiple components) sees
// the same in-session value instead of some instances observing the write and
// others silently reading stale storage.
type UnpersistedEntry = { forRaw: string | null; value: unknown };
const unpersistedFallbacks = new Map<string, UnpersistedEntry>();

const getUnpersistedFallback = (key: string): UnpersistedEntry | null => unpersistedFallbacks.get(key) ?? null;

const setUnpersistedFallback = (key: string, entry: UnpersistedEntry | null) => {
  if (entry) {
    unpersistedFallbacks.set(key, entry);
  } else {
    unpersistedFallbacks.delete(key);
  }
  dispatchStorageEvent();
};

// Test-only escape hatch: the fallback map is module-scoped (that's the point —
// it's what makes it shared across hook instances), so it outlives any single
// test's render tree and must be cleared explicitly between tests alongside
// `window.localStorage.clear()`.
export const resetJsonLocalStorageFallbacksForTesting = () => {
  unpersistedFallbacks.clear();
};

const readRaw = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // localStorage can throw (privacy mode, disabled storage) — treat as absent.
    return null;
  }
};

// Persist a value (removing the key for null/undefined) and notify subscribers.
// Never throws — writes can fail on quota/privacy-mode and must not crash the
// UI. Returns whether the write actually landed, so callers can fall back to
// an in-memory value instead of silently discarding the selection.
const writeRaw = (key: string, value: unknown): boolean => {
  try {
    if (value == null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, JSON.stringify(value));
    }
    dispatchStorageEvent();
    return true;
  } catch {
    return false;
  }
};

/**
 * localStorage-backed state that never throws on tampered or malformed values.
 *
 * The stored string is JSON-parsed defensively and always run through
 * `sanitize`, which must coerce an arbitrary `unknown` into a valid `T` —
 * returning a safe fallback for anything malformed (and for `undefined`, which
 * is passed when the key is missing or unparseable). Writes persist JSON and
 * notify same-tab + cross-tab subscribers; `null`/`undefined` removes the key.
 *
 * Tampered/malformed values never reach the UI (always sanitised first) and are
 * additionally **auto-healed**: a `console.warn` is emitted and the stored value
 * is rewritten to its sanitised form (or removed) so storage self-corrects.
 *
 * Persistence is best-effort: when a write can't land (quota exceeded, privacy
 * mode, storage disabled entirely), the selection still takes effect for the
 * rest of the session via an in-memory fallback — the same as a plain
 * `useState` would have behaved before persistence was added — instead of the
 * control silently getting stuck on its old value.
 *
 * Client-only (admin surfaces are not server-rendered); falls back to
 * `sanitize(undefined)` if ever evaluated on the server.
 */
export function useJsonLocalStorage<T>(key: string, sanitize: (value: unknown) => T) {
  const getSnapshot = useCallback(() => readRaw(key), [key]);
  const raw = useSyncExternalStore(subscribe, getSnapshot, () => null);

  const storedValue = useMemo(() => {
    if (raw === null) {
      return sanitize(undefined);
    }
    try {
      return sanitize(JSON.parse(raw));
    } catch {
      return sanitize(undefined);
    }
  }, [raw, sanitize]);

  // Set only when a write couldn't be persisted, keyed to the `raw` it was
  // written against — so a later successful write (raw changes) or a
  // cross-tab update naturally supersedes it instead of masking real storage
  // forever. Shared across every hook instance for this key via the module-level
  // map (see `unpersistedFallbacks` above), so e.g. a workspace switch made
  // through one mounted instance is immediately visible to sibling instances
  // reading the same key.
  const getUnpersistedSnapshot = useCallback(() => getUnpersistedFallback(key), [key]);
  const unpersisted = useSyncExternalStore(subscribe, getUnpersistedSnapshot, () => null);
  useEffect(() => {
    if (unpersisted && unpersisted.forRaw !== raw) {
      setUnpersistedFallback(key, null);
    }
  }, [raw, key, unpersisted]);

  const value = unpersisted && unpersisted.forRaw === raw ? (unpersisted.value as T) : storedValue;

  // Auto-heal: if the persisted value was malformed/tampered, warn and rewrite it
  // (or remove it) so it self-corrects on the next read. Runs as an effect so the
  // sanitised `value` already shields the render path.
  useEffect(() => {
    if (raw === null) {
      return;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      logger.warn("Removed unparseable localStorage value", { suppressTelemetry: true, attributes: { key } });
      writeRaw(key, undefined);
      return;
    }

    const sanitized = sanitize(parsed);
    if (JSON.stringify(sanitized) === JSON.stringify(parsed)) {
      return;
    }

    logger.warn("Healed malformed localStorage value", { suppressTelemetry: true, attributes: { key } });
    writeRaw(key, sanitized);
  }, [raw, key, sanitize]);

  const setValue = useCallback(
    (next: T | ((current: T) => T)) => {
      const currentRaw = readRaw(key);
      // Prefer a still-live unpersisted fallback over a fresh storage read, so a
      // functional update composes with the last selection made by *any* mounted
      // instance for this key, even while writes keep failing (private mode,
      // storage disabled). Read the shared map directly rather than the render's
      // `unpersisted` closure, which could be stale relative to another instance's
      // concurrent write.
      const liveUnpersisted = getUnpersistedFallback(key);
      let current: T;
      if (liveUnpersisted && liveUnpersisted.forRaw === currentRaw) {
        current = liveUnpersisted.value as T;
      } else {
        try {
          current = sanitize(currentRaw === null ? undefined : JSON.parse(currentRaw));
        } catch {
          current = sanitize(undefined);
        }
      }

      const resolved = typeof next === "function" ? (next as (current: T) => T)(current) : next;
      const persisted = writeRaw(key, resolved);
      setUnpersistedFallback(key, persisted ? null : { forRaw: currentRaw, value: resolved });
    },
    [key, sanitize],
  );

  return [value, setValue] as const;
}
